import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Reveal } from './Reveal';

export default function ProductShowcase() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5], [8, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.95, 1]);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-[#faf9f5] dark:bg-[#141413]">
      <div className="mx-auto max-w-6xl px-6">
        {/* Section header */}
        <Reveal className="mb-16 text-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('showcase.eyebrow', '产品预览')}
          </span>
          <h2 className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('showcase.title')}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
            {t('showcase.description')}
          </p>
        </Reveal>

        {/* 截图区域 — 视差 + 双层 bezel 外壳 */}
        <motion.div
          style={{ y, rotateX, scale, perspective: 1200 }}
          className="relative mx-auto"
        >
          {/* 暖色光晕 */}
          <div className="pointer-events-none absolute -inset-8 rounded-[3rem] bg-[#ae5630]/6 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-4 left-1/4 right-1/4 h-24 rounded-full bg-[#d97757]/10 blur-2xl" />

          {/* 双层 bezel 外壳 */}
          <div className="relative rounded-[1.5rem] border border-[#1414131a] bg-[#1414130a] p-2 shadow-warm-lg dark:border-[#faf9f51a] dark:bg-[#faf9f50a]">
            <div className="overflow-hidden rounded-[calc(1.5rem-0.5rem)] border border-[#1414130d] bg-[#fffefb] shadow-inner-glow dark:border-[#faf9f50d] dark:bg-[#1a1918]">
              {/* 窗口标题栏 — 与 Hero 终端卡保持一致 */}
              <div className="flex items-center gap-2 border-b border-[#1414130d] px-4 py-3 dark:border-[#faf9f50d]">
                <span className="h-2.5 w-2.5 rounded-full bg-[#d98c73]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#d8be8a]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#9fb185]" />
                <span className="ml-2 font-mono text-xs text-[#87867f]">Cherry Agent</span>
              </div>

              {/* 截图网格 — 非对称：左宽右窄 */}
              <div className="grid grid-cols-1 gap-px bg-[#1414130d] md:grid-cols-[3fr_2fr] dark:bg-[#faf9f50d]">
                {/* 截图 1 — 主截图，占更大比例 */}
                <div className="bg-[#fffefb] dark:bg-[#1a1918]">
                  <img
                    src="/zhuomian.png"
                    alt={t('showcase.screenshot1Alt')}
                    className="h-auto w-full"
                    loading="lazy"
                  />
                </div>

                {/* 截图 2 — 辅助截图 */}
                <div className="bg-[#fffefb] dark:bg-[#1a1918]">
                  <img
                    src="/zhuomian2.png"
                    alt={t('showcase.screenshot2Alt')}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
